<template>
  <div class="account-management-page">
    <div class="settings-card">
      <div class="header-section">
        <h2 class="page-title">
          <Icon icon="heroicons:user-group" width="24" height="24" />
          账号管理
        </h2>

        <div class="license-info">
          <div class="license-item">
            <Icon icon="heroicons:key" width="16" height="16" class="license-icon" />
            <span class="label">总授权数：</span>
            <span class="value">{{ licenseInfo.total === -1 ? '无限制' : licenseInfo.total }}</span>
          </div>
          <div class="license-item">
            <Icon icon="heroicons:check-circle" width="16" height="16" class="license-icon success" />
            <span class="label">已使用：</span>
            <span class="value">{{ licenseInfo.used }}</span>
          </div>
          <div class="license-item">
            <Icon icon="heroicons:clock" width="16" height="16" class="license-icon warning" />
            <span class="label">剩余可用：</span>
            <span class="value">{{ licenseInfo.remaining === -1 ? '无限制' : licenseInfo.remaining }}</span>
          </div>
        </div>
      </div>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input v-model="searchForm.ghz_name" placeholder="请输入账号名称" clearable size="large" class="search-input"
            @keyup.enter="handleSearch">
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button type="primary" size="large" @click="handleSearch" class="search-btn">
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>
        </div>

        <div class="search-right">
          <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
            <Icon icon="heroicons:plus" width="18" height="18" />
            <span class="btn-text">新增账号</span>
          </el-button>

          <el-button type="info" size="large" @click="batchCheckAuthorization" :loading="batchCheckLoading"
            class="check-btn">
            <Icon icon="heroicons:arrow-path" width="18" height="18" />
            <span class="btn-text">一键检测RPA授权</span>
          </el-button>

          <el-button type="primary" size="large" @click="openQuickAuthDialog" class="quick-auth-btn">
            <Icon icon="heroicons:bolt" width="18" height="18" />
            <span class="btn-text">极速授权</span>
          </el-button>
        </div>
      </div>

      <!-- 批量操作栏 -->
      <div class="batch-actions" v-if="selectedIds.length > 0">
        <el-button type="primary" size="large" @click="showBatchProxyDialog">
          <Icon icon="heroicons:globe-alt" width="16" height="16" />
          批量设置代理IP (已选 {{ selectedIds.length }} 个)
        </el-button>
        <el-button type="primary" size="large" @click="showBatchCategoryDialog">
          <Icon icon="heroicons:folder" width="16" height="16" />
          批量设置分组 (已选 {{ selectedIds.length }} 个)
        </el-button>
        <button class="clear-selection-btn" @click="clearSelection">
          <Icon icon="heroicons:x-mark" width="16" height="16" />
          取消选择
        </button>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table ref="accountTable" :data="accountList" stripe v-loading="loading" class="account-table"
          @selection-change="handleSelectionChange" :row-style="{ background: 'transparent' }"
          :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">
          <el-table-column type="selection" width="55" />
          <el-table-column prop="ghz_name" label="账号名称" min-width="150" />
          <el-table-column prop="field_type" label="账号类型" width="120">
            <template #default="{ row }">
              <span class="type-tag" :class="getAccountTypeClass(row.field_type)">
                {{ getAccountTypeText(row.field_type) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="分组" width="140">
            <template #default="{ row }">
              <span v-if="row.category_name" class="category-tag">{{ row.category_name }}</span>
              <span v-else class="empty-text">未分组</span>
            </template>
          </el-table-column>

          <el-table-column label="授权方式" width="110">
            <template #default="{ row }">
              <span class="auth-tag" :class="getAuthMethodClass(row.auth_method)">
                {{ getAuthMethodText(row.auth_method) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="开放平台" width="110">
            <template #default="{ row }">
              <div v-if="row.auth_method === 2">
                <button v-if="!row.access_token || row.access_token === 'null' || row.access_token === '[]'"
                  class="action-btn small auth-btn" @click="handleOpenAuth(row)" :disabled="authLoading[row.id]">
                  <Icon icon="heroicons:key" width="14" height="14" />
                  {{ authLoading[row.id] ? '授权中...' : '在线授权' }}
                </button>
                <button v-else class="action-btn small auth-btn success" @click="handleOpenAuth(row)"
                  :disabled="authLoading[row.id]">
                  <Icon icon="heroicons:arrow-path" width="14" height="14" />
                  {{ authLoading[row.id] ? '授权中...' : '重新授权' }}
                </button>
              </div>
              <span v-else class="empty-text">-</span>
            </template>
          </el-table-column>

          <el-table-column label="RPA授权" width="180">
            <template #default="{ row }">
              <div class="rpa-buttons">
                <button v-if="!row.cookies || row.cookies === 'null' || row.cookies === '[]'"
                  class="action-btn small rpa-btn" @click="startAuthorization(row)">
                  <Icon icon="heroicons:shield-check" width="14" height="14" />
                  点击授权
                </button>
                <button v-else class="action-btn small rpa-btn warning" @click="startAuthorization(row)">
                  <Icon icon="heroicons:arrow-path" width="14" height="14" />
                  重新授权
                </button>
                <button :disabled="!row.cookies || row.cookies === 'null' || row.cookies === '[]'"
                  class="action-btn small check-btn" @click="checkAuthorization(row)"
                  :class="{ disabled: !row.cookies || row.cookies === 'null' || row.cookies === '[]' }">
                  <Icon icon="heroicons:check-circle" width="14" height="14" />
                  {{ checkLoading[row.id] ? '检测中...' : '检测授权' }}
                </button>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="IP+端口" width="140">
            <template #default="{ row }">
              <span v-if="row.ip_port" class="info-text">{{ row.ip_port }}</span>
              <span v-else class="empty-text">未设置</span>
            </template>
          </el-table-column>

          <el-table-column label="账号密码" width="160">
            <template #default="{ row }">
              <span v-if="row.ip_password" class="password-text">{{ row.ip_password }}</span>
              <span v-else class="empty-text">未设置</span>
            </template>
          </el-table-column>

          <el-table-column prop="notes" label="备注" min-width="120" show-overflow-tooltip>
            <template #default="{ row }">
              <span class="notes-text">{{ row.notes || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="create_time" label="创建时间" width="170" />
          <el-table-column prop="update_time" label="更新时间" width="170" />

          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <button class="action-btn edit-btn" @click="openEditDialog(scope.row)">
                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                <span class="btn-text">编辑</span>
              </button>
              <button class="action-btn delete-btn" @click="handleDelete(scope.row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout"
          @size-change="fetchAccountList" @current-change="fetchAccountList" background />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" :width="dialogWidth" :close-on-click-modal="false"
      class="custom-dialog" :top="dialogTop">
      <el-form ref="accountForm" :model="accountForm" :label-width="formLabelWidth" :rules="formRules">
        <el-form-item label="账号名称" prop="ghz_name">
          <el-input v-model="accountForm.ghz_name" placeholder="请输入账号名称" size="large" />
        </el-form-item>

        <el-form-item label="账号分组">
          <el-select v-model="accountForm.category_id" placeholder="请选择分组" clearable filterable>
            <el-option v-for="category in categoryOptions" :key="category.id" :label="category.account_category"
              :value="category.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="账号类型" prop="field_type">
          <el-select v-model="accountForm.field_type" placeholder="请选择账号类型" size="large"
            @change="handleAccountTypeChange">
            <el-option v-if="applicationMarket.wyjuzhen_dy" label="某音授权" :value="2" />
            <el-option v-if="applicationMarket.wyjuzhen_ks" label="某手授权" :value="3" />
            <el-option v-if="applicationMarket.wyjuzhen_sph" label="视频号授权" :value="4" />
            <el-option v-if="applicationMarket.wyjuzhen_xhs" label="小红薯授权" :value="5" />
            <el-option v-if="applicationMarket.wyjuzhen_bilibili" label="B站授权" :value="6" />
            <el-option v-if="applicationMarket.wyjuzhen_tiktok" label="TK授权" :value="7" />
          </el-select>
        </el-form-item>

        <el-form-item v-if="accountForm.field_type === 2" label="某音密码" prop="dy_password">
          <el-input v-model="accountForm.dy_password" placeholder="请输入某音密码" size="large" show-password />
          <div class="input-tip">密码必须正确，否则可能导致发送失败！</div>
        </el-form-item>

        <template v-if="accountForm.field_type === 1 && accountForm.auth_method === 1">
          <el-form-item label="AppID" prop="ghz_appid">
            <el-input v-model="accountForm.ghz_appid" placeholder="请输入账号AppID" size="large" />
          </el-form-item>

          <el-form-item label="Secret" prop="ghz_secret">
            <el-input v-model="accountForm.ghz_secret" placeholder="请输入账号Secret" size="large" show-password />
          </el-form-item>
        </template>

        <el-form-item label="IP+端口">
          <el-input v-model="accountForm.ip_port" placeholder="格式：232.33.1.1:16817（可选）" size="large" />
          <div class="input-tip">非必填项，可留空</div>
        </el-form-item>

        <el-form-item label="账号密码">
          <el-input v-model="accountForm.ip_password" placeholder="格式：账号:密码，如：34123:55332（可选）" size="large"
            show-password />
          <div class="input-tip">非必填项，可留空</div>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="accountForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitAccountForm">确认</button>
      </template>
    </el-dialog>

    <!-- 批量设置代理IP对话框 -->
    <el-dialog v-model="batchProxyDialogVisible" title="批量设置代理IP" :width="dialogWidth" class="custom-dialog">
      <el-form :model="batchProxyForm" label-width="100px">
        <el-form-item label="IP+端口">
          <el-input v-model="batchProxyForm.ip_port" placeholder="格式：232.33.1.1:16817" />
        </el-form-item>
        <el-form-item label="账号密码">
          <el-input v-model="batchProxyForm.ip_password" placeholder="格式：账号:密码，如：34123:55332" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="batchProxyDialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitBatchProxy" :disabled="batchProxyLoading">
          {{ batchProxyLoading ? '设置中...' : '确认设置' }}
        </button>
      </template>
    </el-dialog>

    <!-- 批量设置分组对话框 -->
    <el-dialog v-model="batchCategoryDialogVisible" title="批量设置分组" :width="dialogWidth" class="custom-dialog">
      <el-form :model="batchCategoryForm" label-width="100px">
        <el-form-item label="选择分组">
          <el-select v-model="batchCategoryForm.category_id" placeholder="请选择分组" clearable>
            <el-option v-for="category in categoryOptions" :key="category.id" :label="category.account_category"
              :value="category.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="batchCategoryDialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitBatchCategory" :disabled="batchCategoryLoading">
          {{ batchCategoryLoading ? '设置中...' : '确认设置' }}
        </button>
      </template>
    </el-dialog>

    <!-- 极速授权对话框 -->
    <el-dialog v-model="quickAuthDialogVisible" title="极速授权" :width="quickAuthDialogWidth"
      class="custom-dialog quick-auth-dialog">
      <div class="quick-auth-container">
        <p class="dialog-tip">点击下方链接即可快速授权，支持扫码绑定账号</p>
        <div class="auth-link-list">
          <div class="auth-link-item"
            v-if="applicationMarket.wyjuzhen_dy || applicationMarket.wyjuzhen_ks || applicationMarket.wyjuzhen_sph || applicationMarket.wyjuzhen_xhs || applicationMarket.wyjuzhen_bilibili || applicationMarket.wyjuzhen_tiktok">
            <div class="link-info">
              <div class="platform-icon all-platform">
                <Icon icon="heroicons:globe-alt" width="22" height="22" />
              </div>
              <div class="link-content">
                <div class="platform-name">全平台极速授权</div>
                <div class="link-url">{{ quickAuthLinks.all }}</div>
              </div>
            </div>
            <button class="copy-btn" @click="copyToClipboard(quickAuthLinks.all, '全平台授权链接')">
              <Icon icon="heroicons:clipboard-document" width="16" height="16" />
              复制
            </button>
          </div>

          <div class="auth-link-item" v-if="applicationMarket.wyjuzhen_dy">
            <div class="link-info">
              <div class="platform-icon douyin">
                <Icon icon="ri:tiktok-fill" width="22" height="22" />
              </div>
              <div class="link-content">
                <div class="platform-name">抖音极速授权</div>
                <div class="link-url">{{ quickAuthLinks.dy }}</div>
              </div>
            </div>
            <button class="copy-btn" @click="copyToClipboard(quickAuthLinks.dy, '抖音授权链接')">复制</button>
          </div>

          <div class="auth-link-item" v-if="applicationMarket.wyjuzhen_ks">
            <div class="link-info">
              <div class="platform-icon kuaishou">
                <Icon icon="ri:live-fill" width="22" height="22" />
              </div>
              <div class="link-content">
                <div class="platform-name">快手极速授权</div>
                <div class="link-url">{{ quickAuthLinks.ks }}</div>
              </div>
            </div>
            <button class="copy-btn" @click="copyToClipboard(quickAuthLinks.ks, '快手授权链接')">复制</button>
          </div>

          <div class="auth-link-item" v-if="applicationMarket.wyjuzhen_sph">
            <div class="link-info">
              <div class="platform-icon video-channel">
                <Icon icon="ri:wechat-fill" width="22" height="22" />
              </div>
              <div class="link-content">
                <div class="platform-name">视频号极速授权</div>
                <div class="link-url">{{ quickAuthLinks.sph }}</div>
              </div>
            </div>
            <button class="copy-btn" @click="copyToClipboard(quickAuthLinks.sph, '视频号授权链接')">复制</button>
          </div>

          <div class="auth-link-item" v-if="applicationMarket.wyjuzhen_xhs">
            <div class="link-info">
              <div class="platform-icon xiaohongshu">
                <Icon icon="ri:leaf-fill" width="22" height="22" />
              </div>
              <div class="link-content">
                <div class="platform-name">小红书极速授权</div>
                <div class="link-url">{{ quickAuthLinks.xhs }}</div>
              </div>
            </div>
            <button class="copy-btn" @click="copyToClipboard(quickAuthLinks.xhs, '小红书授权链接')">复制</button>
          </div>

          <div class="auth-link-item" v-if="applicationMarket.wyjuzhen_bilibili">
            <div class="link-info">
              <div class="platform-icon bilibili">
                <Icon icon="ri:bilibili-fill" width="22" height="22" />
              </div>
              <div class="link-content">
                <div class="platform-name">B站极速授权</div>
                <div class="link-url">{{ quickAuthLinks.bili }}</div>
              </div>
            </div>
            <button class="copy-btn" @click="copyToClipboard(quickAuthLinks.bili, 'B站授权链接')">复制</button>
          </div>

          <div class="auth-link-item" v-if="applicationMarket.wyjuzhen_tiktok">
            <div class="link-info">
              <div class="platform-icon tiktok">
                <Icon icon="ri:tiktok-fill" width="22" height="22" />
              </div>
              <div class="link-content">
                <div class="platform-name">Tiktok极速授权</div>
                <div class="link-url">{{ quickAuthLinks.tt }}</div>
              </div>
            </div>
            <button class="copy-btn" @click="copyToClipboard(quickAuthLinks.tt, 'Tiktok授权链接')">复制</button>
          </div>
        </div>
      </div>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="quickAuthDialogVisible = false">关闭</button>
      </template>
    </el-dialog>

    <!-- QR Code 授权对话框 -->
    <el-dialog v-model="qrDialogVisible" title="账号授权" width="400px" class="custom-dialog qr-dialog"
      :close-on-click-modal="false">
      <div class="qr-code-container">
        <div v-if="qrLoading" class="loading-container">
          <Icon icon="heroicons:arrow-path" width="30" height="30" class="loading-icon" />
          <span>正在获取二维码...</span>
        </div>
        <template v-if="!qrLoading">
          <img v-if="qrCodeBase64" :src="qrCodeBase64" alt="账号授权二维码" class="qr-code-image" />
          <div v-if="showVerificationCode" class="verification-section">
            <el-alert title="需要短信验证码" type="warning" :closable="false" />
            <el-input v-model="verificationCode" :placeholder="'请输入验证码 (' + verificationElement + ')'" clearable
              @keyup.enter="confirmAuthorization" />
          </div>
        </template>
        <div class="qr-instructions">
          <p v-if="!showVerificationCode" class="auth-instruction">
            请使用 <strong>{{ getPlatformName(currentAuthRow?.field_type) }}</strong> APP 扫描上方二维码授权账号
          </p>
          <p v-else>请输入收到的短信验证码</p>
          <p>完成后点击下方确认按钮</p>
        </div>
      </div>
      <template #footer>
        <button class="dialog-btn cancel-btn" @click="qrDialogVisible = false">取消</button>
        <el-button class="dialog-btn confirm-btn" @click="confirmAuthorization"
          :disabled="!qrCodeBase64 || (showVerificationCode && !verificationCode)" :loading="confirmLoading">
          {{ showVerificationCode ? '提交验证码' : '确认授权' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'AccountManagement',
  components: { Icon },
  data() {
    return {
      loading: false,
      checkLoading: {},
      batchCheckLoading: false,
      dialogVisible: false,
      dialogTitle: '新增账号',
      isEditMode: false,
      qrDialogVisible: false,
      qrLoading: false,
      confirmLoading: false,
      qrCodeBase64: '',
      currentAuthRow: null,
      webId: '',
      fingerprint: '',
      authLoading: {},
      selectedIds: [],
      batchProxyDialogVisible: false,
      batchProxyLoading: false,
      showVerificationCode: false,
      verificationCode: '',
      verificationElement: '',
      batchProxyForm: { ip_port: '', ip_password: '' },
      batchCategoryDialogVisible: false,
      batchCategoryLoading: false,
      batchCategoryForm: { category_id: null },
      categoryOptions: [],
      quickAuthDialogVisible: false,
      quickAuthLinks: { all: '', dy: '', ks: '', sph: '', xhs: '', bili: '', tt: '' },
      screenWidth: window.innerWidth,
      searchForm: { ghz_name: '' },
      accountForm: {
        id: null, category_id: null, ghz_name: '', field_type: 2, dy_password: '',
        ip_port: '', ip_password: '', auth_method: 3, ghz_appid: '', ghz_secret: '', notes: ''
      },
      formRules: {
        ghz_name: [{ required: true, message: '请输入账号名称', trigger: 'blur' }],
        field_type: [{ required: true, message: '请选择账号类型', trigger: 'change' }],
        dy_password: [{ required: () => this.accountForm.field_type === 2, message: '请输入某音密码', trigger: 'blur' }]
      },
      accountList: [],
      pagination: { page: 1, pageSize: 10, total: 0 },
      licenseInfo: { total: 0, used: 0, remaining: 0 },
      applicationMarket: {
        wyjuzhen_dy: false, wyjuzhen_ks: false, wyjuzhen_sph: false,
        wyjuzhen_xhs: false, wyjuzhen_bilibili: false, wyjuzhen_tiktok: false
      }
    }
  },
  computed: {
    userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next'
      if (this.screenWidth < 768) return 'total, prev, pager, next'
      return 'total, sizes, prev, pager, next, jumper'
    },
    dialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '600px'
    },
    quickAuthDialogWidth() {
      if (this.screenWidth < 576) return '95%'
      if (this.screenWidth < 768) return '90%'
      return '500px'
    },
    dialogTop() {
      if (this.screenWidth < 576) return '5vh'
      return '10vh'
    },
    formLabelWidth() {
      if (this.screenWidth < 576) return '80px'
      return '100px'
    }
  },
  mounted() {
    this.fetchAccountList();
    this.fetchLicenseInfo();
    this.fetchApplicationMarket();
    this.fetchCategories();
    this.buildQuickAuthLinks();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() { this.screenWidth = window.innerWidth },
    buildQuickAuthLinks() {
      const baseUrl = window.location.origin;
      const sqUrl = `${baseUrl}/shop/sq.html`;
      const storeId = this.userInfo.id;
      this.quickAuthLinks = {
        all: `${sqUrl}?store_id=${storeId}&platform=all`,
        dy: `${sqUrl}?store_id=${storeId}&platform=dy`,
        ks: `${sqUrl}?store_id=${storeId}&platform=ks`,
        sph: `${sqUrl}?store_id=${storeId}&platform=sph`,
        xhs: `${sqUrl}?store_id=${storeId}&platform=xhs`,
        bili: `${sqUrl}?store_id=${storeId}&platform=bili`,
        tt: `${sqUrl}?store_id=${storeId}&platform=tt`
      };
    },
    openQuickAuthDialog() {
      this.buildQuickAuthLinks();
      this.quickAuthDialogVisible = true;
    },
    copyToClipboard(text, name) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        ElMessage.success(`${name}已复制到剪贴板`);
      } catch (err) {
        ElMessage.error('复制失败，请手动复制');
      } finally {
        document.body.removeChild(textarea);
      }
    },
    getPlatformName(type) {
      const names = { 1: '微信', 2: '抖音', 3: '快手', 4: '视频号', 5: '小红书', 6: 'B站', 7: 'TikTok' };
      return names[type] || '对应平台';
    },
    getAccountTypeText(type) {
      const types = { 1: '公众号', 2: '某音', 3: '某手', 4: '视频号', 5: '小红薯', 6: 'B站', 7: 'Tiktok' };
      return types[type] || '未知类型';
    },
    getAccountTypeClass(type) {
      const classes = { 1: 'wechat', 2: 'douyin', 3: 'kuaishou', 4: 'video-channel', 5: 'xiaohongshu', 6: 'bilibili', 7: 'tiktok' };
      return classes[type] || 'default';
    },
    getAuthMethodText(method) {
      const methods = { 1: '密钥授权', 2: '开放平台', 3: 'RPA授权' };
      return methods[method] || '-';
    },
    getAuthMethodClass(method) {
      const classes = { 1: 'secret', 2: 'open', 3: 'rpa' };
      return classes[method] || '';
    },
    async fetchCategories() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/account_category_management.php`, {
          action: 'list_all', store_id: this.userInfo.id
        });
        if (data.data) this.categoryOptions = data.data;
      } catch (error) { console.error('获取分组列表失败:', error); }
    },
    handleSelectionChange(selection) { this.selectedIds = selection.map(item => item.id); },
    clearSelection() { if (this.$refs.accountTable) this.$refs.accountTable.clearSelection(); this.selectedIds = []; },
    showBatchProxyDialog() {
      this.batchProxyForm = { ip_port: '', ip_password: '' };
      this.batchProxyDialogVisible = true;
    },
    async submitBatchProxy() {
      if (this.selectedIds.length === 0) { ElMessage.warning('请先选择要设置的账号'); return; }
      this.batchProxyLoading = true;
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, {
          action: 'batch_set_proxy', ids: this.selectedIds,
          ip_port: this.batchProxyForm.ip_port, ip_password: this.batchProxyForm.ip_password, store_id: this.userInfo.id
        });
        if (data.success) {
          ElMessage.success(`成功为 ${this.selectedIds.length} 个账号设置代理`);
          this.batchProxyDialogVisible = false;
          this.clearSelection();
          this.fetchAccountList();
        } else { ElMessage.error(data.error || '批量设置代理失败'); }
      } catch (error) { ElMessage.error('批量设置代理失败'); } finally { this.batchProxyLoading = false; }
    },
    showBatchCategoryDialog() {
      this.batchCategoryForm = { category_id: null };
      this.batchCategoryDialogVisible = true;
    },
    async submitBatchCategory() {
      if (this.selectedIds.length === 0) { ElMessage.warning('请先选择要设置的账号'); return; }
      this.batchCategoryLoading = true;
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, {
          action: 'batch_set_category', ids: this.selectedIds,
          category_id: this.batchCategoryForm.category_id, store_id: this.userInfo.id
        });
        if (data.success) {
          ElMessage.success(`成功为 ${this.selectedIds.length} 个账号设置分组`);
          this.batchCategoryDialogVisible = false;
          this.clearSelection();
          this.fetchAccountList();
        } else { ElMessage.error(data.error || '批量设置分组失败'); }
      } catch (error) { ElMessage.error('批量设置分组失败'); } finally { this.batchCategoryLoading = false; }
    },
    async fetchAccountList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.ghz_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };
        if (this.searchForm.ghz_name) params.ghz_name = this.searchForm.ghz_name;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, params);
        if (data.data) {
          this.accountList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) { ElMessage.error('获取账号列表失败'); } finally { this.loading = false; }
    },
    handleSearch() { this.pagination.page = 1; this.fetchAccountList(); },
    async fetchLicenseInfo() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, {
          action: 'license_info', store_id: this.userInfo.id
        });
        if (data && !data.error) this.licenseInfo = { total: data.total || 0, used: data.used || 0, remaining: data.remaining || 0 };
      } catch (error) { console.error('获取授权信息失败:', error); }
    },
    async fetchApplicationMarket() {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/shop_api.php`, {
          action: 'query', account: this.userInfo.account
        });
        if (response.data.data?.application_market) {
          const market = JSON.parse(response.data.data.application_market);
          this.applicationMarket = {
            wyjuzhen_dy: market.wyjuzhen_dy || false, wyjuzhen_ks: market.wyjuzhen_ks || false,
            wyjuzhen_sph: market.wyjuzhen_sph || false, wyjuzhen_xhs: market.wyjuzhen_xhs || false,
            wyjuzhen_bilibili: market.wyjuzhen_bilibili || false, wyjuzhen_tiktok: market.wyjuzhen_tiktok || false
          };
        }
      } catch (error) { console.error('获取应用市场数据失败:', error); }
    },
    handleAccountTypeChange(type) {
      this.accountForm.auth_method = type !== 1 ? 3 : 1;
      if (type !== 2) this.accountForm.dy_password = '';
      if (type !== 1) { this.accountForm.ghz_appid = ''; this.accountForm.ghz_secret = ''; }
    },
    openAddDialog() {
      if (this.licenseInfo.total !== -1 && this.licenseInfo.remaining <= 0) {
        ElMessage.warning('授权账号数已满，请联系管理员充值！');
        return;
      }
      let defaultType = 2;
      if (this.applicationMarket.wyjuzhen_dy) defaultType = 2;
      else if (this.applicationMarket.wyjuzhen_ks) defaultType = 3;
      else if (this.applicationMarket.wyjuzhen_sph) defaultType = 4;
      else if (this.applicationMarket.wyjuzhen_xhs) defaultType = 5;
      else if (this.applicationMarket.wyjuzhen_bilibili) defaultType = 6;
      else if (this.applicationMarket.wyjuzhen_tiktok) defaultType = 7;
      this.dialogTitle = '新增账号';
      this.isEditMode = false;
      this.accountForm = {
        id: null, category_id: null, ghz_name: '', field_type: defaultType, dy_password: '',
        ip_port: '', ip_password: '', auth_method: defaultType === 1 ? 1 : 3,
        ghz_appid: '', ghz_secret: '', notes: ''
      };
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      this.dialogTitle = '编辑账号';
      this.isEditMode = true;
      this.accountForm = {
        id: row.id, category_id: row.category_id, ghz_name: row.ghz_name, field_type: row.field_type,
        dy_password: row.dy_password || '', ip_port: row.ip_port, ip_password: row.ip_password,
        auth_method: row.auth_method, ghz_appid: row.ghz_appid, ghz_secret: row.ghz_secret, notes: row.notes
      };
      this.dialogVisible = true;
    },
    async submitAccountForm() {
      this.$refs.accountForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = { ...this.accountForm, store_id: this.userInfo.id };
            if (payload.auth_method !== 1) { payload.ghz_appid = ''; payload.ghz_secret = ''; }
            const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, { action, ...payload });
            if (data.success) {
              ElMessage.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchAccountList();
              this.fetchLicenseInfo();
            } else { ElMessage.error(data.error || '操作失败'); }
          } catch (error) { ElMessage.error('操作失败'); }
        }
      });
    },
    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除该账号吗？', '警告', { confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning' });
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, {
          action: 'delete', id, store_id: this.userInfo.id
        });
        if (data.success) {
          ElMessage.success('删除成功');
          this.fetchAccountList();
          this.fetchLicenseInfo();
        } else { ElMessage.error(data.error || '删除失败'); }
      } catch (error) { if (error !== 'cancel') ElMessage.error('删除失败'); }
    },
    async startAuthorization(row) {
      this.currentAuthRow = row;
      this.qrDialogVisible = true;
      this.qrLoading = true;
      this.qrCodeBase64 = '';
      this.showVerificationCode = false;
      this.verificationCode = '';
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_cookies.php`, {
          action: 'get_qr', id: row.id, store_id: this.userInfo.id, field_type: row.field_type
        });
        if (data.status === 'success') {
          this.qrCodeBase64 = data.base64;
          this.webId = data.web_id;
          this.fingerprint = data.fingerprint;
        } else { ElMessage.error(data.error || '获取二维码失败'); }
      } catch (error) { ElMessage.error('获取二维码失败'); } finally { this.qrLoading = false; }
    },
    async confirmAuthorization() {
      this.confirmLoading = true;
      try {
        const payload = {
          action: 'update_cookies', web_id: this.webId, fingerprint: this.fingerprint,
          id: this.currentAuthRow.id, store_id: this.userInfo.id, field_type: this.currentAuthRow.field_type
        };
        if (this.showVerificationCode) payload.verification_code = this.verificationCode;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_cookies.php`, payload);
        if (data.status === 'success') {
          ElMessage.success('授权成功');
          this.qrDialogVisible = false;
          this.fetchAccountList();
        } else if (data.status === 'verification_required') {
          this.showVerificationCode = true;
          this.verificationElement = data.verification_element || '#button-input';
          ElMessage.warning('需要短信验证码才能继续');
        } else { ElMessage.error(data.message || '授权失败'); }
      } catch (error) { ElMessage.error('授权失败'); } finally { this.confirmLoading = false; }
    },
    async checkAuthorization(row) {
      this.checkLoading = { ...this.checkLoading, [row.id]: true };
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/update_cookies_by_ids.php`, {
          ids: [row.id], field_type: row.field_type
        });
        if (data.success) {
          const result = data.results.find(r => r.id === row.id);
          if (result) {
            if (result.status === 'success') ElMessage.success(`ID ${row.id} 授权有效`);
            else if (result.status === 'cooldown') ElMessage.warning(`ID ${row.id} 处于冷却期`);
            else ElMessage.warning(`ID ${row.id} ${result.message}`);
          }
        } else { ElMessage.error(data.error || '检测授权失败'); }
      } catch (error) { ElMessage.error('检测授权失败'); } finally {
        this.checkLoading = { ...this.checkLoading, [row.id]: false };
        this.fetchAccountList();
      }
    },
    async batchCheckAuthorization() {
      const validIds = this.accountList.filter(item => item.cookies && item.cookies !== 'null' && item.cookies !== '[]').map(item => ({ id: item.id, field_type: item.field_type }));
      if (validIds.length === 0) { ElMessage.warning('没有可检测的授权账号'); return; }
      this.batchCheckLoading = true;
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/update_cookies_by_ids.php`, { ids: validIds });
        if (data.success) {
          const stats = { success: 0, cooldown: 0, error: 0 };
          data.results.forEach(result => {
            if (result.status === 'success') stats.success++;
            else if (result.status === 'cooldown') stats.cooldown++;
            else stats.error++;
          });
          ElMessage.success(`检测完成 - 成功: ${stats.success}个, 冷却中: ${stats.cooldown}个, 失败: ${stats.error}个`);
        } else { ElMessage.error(data.error || '批量检测失败'); }
      } catch (error) { ElMessage.error('批量检测失败'); } finally {
        this.batchCheckLoading = false;
        this.fetchAccountList();
      }
    },
    async handleOpenAuth(row) {
      this.authLoading = { ...this.authLoading, [row.id]: true };
      const authUrl = `${config.BASE_URL}/weixin/get_auth_url.php?id=${row.id}&store_id=${this.userInfo.id}`;
      window.open(authUrl, '_blank');
      setTimeout(() => { this.authLoading = { ...this.authLoading, [row.id]: false }; }, 10000);
    }

  }
}
</script>

<style scoped lang="scss">
.account-management-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;

  svg {
    color: #c084fc;
  }

  @media (max-width: 768px) {
    font-size: 18px;
  }
}

.license-info {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  background: rgba(157, 78, 221, 0.1);
  padding: 8px 16px;
  border-radius: 12px;
  border: 1px solid rgba(157, 78, 221, 0.2);

  .license-item {
    display: flex;
    align-items: center;
    gap: 6px;

    .license-icon {
      color: #9d4edd;

      &.success {
        color: #4ade80;
      }

      &.warning {
        color: #fbbf24;
      }
    }

    .label {
      color: #a0a0c0;
      font-size: 13px;
    }

    .value {
      color: #fff;
      font-weight: 600;
    }
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;

  .search-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
  }

  .search-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    .search-left,
    .search-right {
      width: 100%;
    }

    .search-left .el-input {
      width: 100% !important;
    }

    .search-left .search-btn,
    .search-right .el-button {
      width: 100%;
    }
  }
}

.search-input {
  flex: 1;
  min-width: 200px;
}

.batch-actions {
  margin-bottom: 20px;
  padding: 12px 16px;
  background: rgba(157, 78, 221, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  .clear-selection-btn {
    background: transparent;
    border: none;
    color: #f87171;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    padding: 6px 12px;
    border-radius: 8px;
    transition: all 0.2s;

    &:hover {
      color: #ef4444;
      background: rgba(239, 68, 68, 0.1);
    }
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(157, 78, 221, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(157, 78, 221, 0.4);
    border-radius: 4px;

    &:hover {
      background: rgba(157, 78, 221, 0.6);
    }
  }
}

/* 表格样式 - 完全覆盖 Element Plus 默认样式 */
:deep(.el-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;

  background-color: transparent !important;
  color: #e0e0e0;

  &::before {
    display: none;
  }

  // 表头行
  .el-table__header-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      // 普通表头（排除固定列）
      th:not(.el-table-fixed-column--right) {
        background: rgba(157, 78, 221, 0.2) !important;
        color: #fff !important;
        font-weight: 600;
        border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

        .cell {
          color: #fff !important;
        }
      }
    }
  }

  // 表体
  .el-table__body-wrapper {
    background-color: transparent !important;

    tr {
      background-color: transparent !important;

      // 普通 td（排除固定列）
      td:not(.el-table-fixed-column--right) {
        background-color: transparent !important;
        border-bottom: 1px solid rgba(157, 78, 221, 0.1);
        color: #e0e0e0;
      }

      // 斑马纹行（排除固定列）
      &.el-table__row--striped td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.05) !important;
      }

      // hover 行（排除固定列）
      &:hover td:not(.el-table-fixed-column--right) {
        background-color: rgba(157, 78, 221, 0.1) !important;
      }
    }
  }

  // 空数据
  .el-table__empty-block {
    background-color: transparent !important;

    .el-table__empty-text {
      color: #a0a0c0 !important;
    }
  }

  // 展开行
  .el-table__expanded-cell {
    background-color: transparent !important;
  }

  // 加载遮罩
  .el-loading-mask {
    background-color: rgba(18, 14, 33, 0.8);
  }
}

/* ========== 固定列（操作列）背景不透明修复 ========== */
/* 注意：这些样式写在 :deep(.el-table) 外面，避免被内部 td 样式覆盖 */
:deep(.el-table-fixed-column--right) {
  background-color: #120e21 !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

// 表头固定列
:deep(th.el-table-fixed-column--right) {
  background-color: #2a1f45 !important;
}

// 斑马纹行的固定列
:deep(.el-table__row--striped .el-table-fixed-column--right) {
  background-color: #1a1430 !important;
}




/* 类型标签样式 */
.type-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;

  &.wechat {
    background: rgba(7, 193, 96, 0.15);
    color: #4ade80;
  }

  &.douyin {
    background: rgba(254, 44, 85, 0.15);
    color: #f87171;
  }

  &.kuaishou {
    background: rgba(255, 86, 0, 0.15);
    color: #fbbf24;
  }

  &.video-channel {
    background: rgba(7, 193, 96, 0.15);
    color: #4ade80;
  }

  &.xiaohongshu {
    background: rgba(255, 36, 66, 0.15);
    color: #f87171;
  }

  &.bilibili {
    background: rgba(251, 114, 153, 0.15);
    color: #fbbf24;
  }

  &.tiktok {
    background: rgba(254, 44, 85, 0.15);
    color: #f87171;
  }

  &.default {
    background: rgba(157, 78, 221, 0.15);
    color: #c084fc;
  }
}

/* 分组标签样式 */
.category-tag {
  display: inline-block;
  padding: 2px 8px;
  background: rgba(157, 78, 221, 0.15);
  border-radius: 4px;
  font-size: 12px;
  color: #c084fc;
}

/* 授权方式标签样式 */
.auth-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;

  &.secret {
    background: rgba(68, 189, 50, 0.15);
    color: #4ade80;
  }

  &.open {
    background: rgba(68, 189, 50, 0.15);
    color: #4ade80;
  }

  &.rpa {
    background: rgba(245, 158, 11, 0.15);
    color: #fbbf24;
  }
}

/* 文本样式 */
.info-text {
  color: #4ade80;
  font-size: 13px;
}

.password-text {
  color: #fbbf24;
  font-size: 13px;
}

.notes-text {
  color: #a0a0c0;
  font-size: 13px;
}

.empty-text {
  color: #7a7a9a;
}

/* 按钮样式 - 通用 */
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin: 0 4px;

  &.small {
    padding: 4px 8px;
    font-size: 11px;
  }

  // 编辑按钮
  &.edit-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      border-color: rgba(157, 78, 221, 0.6);
      transform: translateY(-1px);
    }
  }

  // 删除按钮
  &.delete-btn {
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #f87171;

    &:hover {
      background: rgba(239, 68, 68, 0.25);
      border-color: rgba(239, 68, 68, 0.6);
      transform: translateY(-1px);
    }
  }

  // 开放平台授权按钮
  &.auth-btn {
    background: rgba(68, 189, 50, 0.15);
    border: 1px solid rgba(68, 189, 50, 0.4);
    color: #4ade80;

    &:hover:not(:disabled) {
      background: rgba(68, 189, 50, 0.25);
      border-color: rgba(68, 189, 50, 0.6);
      transform: translateY(-1px);
    }

    &.success {
      background: rgba(245, 158, 11, 0.15);
      border-color: rgba(245, 158, 11, 0.4);
      color: #fbbf24;

      &:hover:not(:disabled) {
        background: rgba(245, 158, 11, 0.25);
        border-color: rgba(245, 158, 11, 0.6);
      }
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  // RPA授权按钮
  &.rpa-btn {
    background: rgba(245, 158, 11, 0.15);
    border: 1px solid rgba(245, 158, 11, 0.4);
    color: #fbbf24;

    &:hover {
      background: rgba(245, 158, 11, 0.25);
      border-color: rgba(245, 158, 11, 0.6);
      transform: translateY(-1px);
    }

    &.warning {
      background: rgba(239, 68, 68, 0.15);
      border-color: rgba(239, 68, 68, 0.4);
      color: #f87171;

      &:hover {
        background: rgba(239, 68, 68, 0.25);
        border-color: rgba(239, 68, 68, 0.6);
      }
    }
  }

  // 检测授权按钮
  &.check-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #c084fc;

    &:hover:not(:disabled) {
      background: rgba(157, 78, 221, 0.25);
      border-color: rgba(157, 78, 221, 0.6);
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  // 小屏幕隐藏文字
  @media (max-width: 576px) {
    .btn-text {
      display: none;
    }
  }
}

/* RPA授权按钮组 */
.rpa-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* 分页样式 */
.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 20px;
  }

  @media (max-width: 576px) {
    margin-top: 16px;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;

  .btn-prev,
  .btn-next,
  .el-pager li {
    background: rgba(157, 78, 221, 0.1);
    border: 1px solid rgba(157, 78, 221, 0.3);
    color: #a0a0c0;
    border-radius: 8px;
    min-width: 32px;
    height: 32px;
    transition: all 0.2s;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.active {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
      border-color: transparent;
    }
  }

  .el-pagination__total,
  .el-pagination__jump {
    color: #a0a0c0;
  }

  .el-select .el-input__wrapper {
    background: rgba(157, 78, 221, 0.1);
    border-color: rgba(157, 78, 221, 0.3);
  }

  @media (max-width: 576px) {

    .btn-prev,
    .btn-next,
    .el-pager li {
      min-width: 28px;
      height: 28px;
      margin: 0 2px;
    }

    .el-pagination__total,
    .el-pagination__jump {
      font-size: 12px;
    }
  }
}

/* 对话框样式 */
:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;

  .el-dialog {
    @media (max-width: 576px) {
      .el-dialog__header {
        padding: 16px;
      }

      .el-dialog__body {
        padding: 16px;
      }

      .el-dialog__footer {
        padding: 12px 16px;
      }
    }
  }

  .el-dialog__header {
    border-bottom: 1px solid rgba(157, 78, 221, 0.2);
    padding: 20px 24px;
    margin-right: 0;

    .el-dialog__title {
      color: #fff;
      font-weight: 600;
      font-size: 18px;

      @media (max-width: 768px) {
        font-size: 16px;
      }
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    border-top: 1px solid rgba(157, 78, 221, 0.2);
    padding: 16px 24px;
  }
}

/* 表单样式 */
:deep(.el-form-item__label) {
  color: #a0a0c0;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 576px) {
    font-size: 12px;
  }
}

:deep(.el-input__wrapper) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  box-shadow: none;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &.is-focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  .el-input__inner {
    color: #fff;
    background: transparent;

    &::placeholder {
      color: #5a5a7a;
    }
  }
}

:deep(.el-textarea__inner) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  color: #fff;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(157, 78, 221, 0.6);
  }

  &:focus {
    border-color: #9d4edd;
    box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
  }

  &::placeholder {
    color: #5a5a7a;
  }
}

:deep(.el-select) {
  .el-input__wrapper {
    background: rgba(26, 21, 53, 0.8);
  }
}

:deep(.el-select-dropdown) {
  background: rgba(18, 14, 33, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 12px;

  .el-select-dropdown__item {
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.2);
      color: #c084fc;
    }

    &.selected {
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      color: #fff;
    }
  }
}

.input-tip {
  font-size: 11px;
  color: #7a7a9a;
  margin-top: 4px;
}

/* Element Plus 按钮样式 */
:deep(.el-button) {
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  transition: all 0.2s;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }
}

:deep(.el-button--primary) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  border: none;

  &:hover {
    background: linear-gradient(135deg, #7556c4, #ad5eed);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
  }
}

:deep(.el-button--success) {
  background: linear-gradient(135deg, #10b981, #34d399);
  border: none;

  &:hover {
    background: linear-gradient(135deg, #059669, #10b981);
    transform: translateY(-1px);
  }
}

:deep(.el-button--info) {
  background: rgba(157, 78, 221, 0.2);
  border: 1px solid rgba(157, 78, 221, 0.4);
  color: #a0a0c0;

  &:hover {
    background: rgba(157, 78, 221, 0.3);
    border-color: rgba(157, 78, 221, 0.6);
    color: #c084fc;
  }
}

/* 对话框按钮 */
.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }

  &.cancel-btn {
    background: rgba(157, 78, 221, 0.15);
    border: 1px solid rgba(157, 78, 221, 0.4);
    color: #a0a0c0;

    &:hover {
      background: rgba(157, 78, 221, 0.25);
      border-color: rgba(157, 78, 221, 0.6);
      color: #c084fc;
    }
  }

  &.confirm-btn {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    color: #fff;

    &:hover {
      background: linear-gradient(135deg, #7556c4, #ad5eed);
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
    }
  }
}

/* 极速授权对话框 */
.quick-auth-dialog {
  .quick-auth-container {
    .dialog-tip {
      color: #a0a0c0;
      font-size: 14px;
      margin-bottom: 20px;
      text-align: center;
      padding: 8px;
      background: rgba(157, 78, 221, 0.1);
      border-radius: 6px;
    }
  }

  .auth-link-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 400px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(157, 78, 221, 0.1);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(157, 78, 221, 0.4);
      border-radius: 3px;
    }
  }

  .auth-link-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: rgba(157, 78, 221, 0.1);
    border-radius: 12px;
    transition: all 0.2s;

    &:hover {
      background: rgba(157, 78, 221, 0.15);
      transform: translateX(4px);
    }

    .link-info {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      overflow: hidden;
    }

    .platform-icon {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: #fff;
      flex-shrink: 0;
    }

    .link-content {
      flex: 1;
      overflow: hidden;

      .platform-name {
        font-weight: 600;
        color: #fff;
        margin-bottom: 4px;
      }

      .link-url {
        font-size: 11px;
        color: #7a7a9a;
        word-break: break-all;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .copy-btn {
      background: rgba(157, 78, 221, 0.2);
      border: 1px solid rgba(157, 78, 221, 0.4);
      color: #c084fc;
      padding: 6px 12px;
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      transition: all 0.2s;
      flex-shrink: 0;

      &:hover {
        background: rgba(157, 78, 221, 0.3);
        border-color: rgba(157, 78, 221, 0.6);
        transform: scale(1.02);
      }
    }
  }
}

/* 二维码授权对话框 */
.qr-dialog {
  .qr-code-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .qr-code-image {
    width: 200px;
    height: 200px;
    margin-bottom: 20px;
    border-radius: 12px;
    border: 1px solid rgba(157, 78, 221, 0.3);
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 0;

    .loading-icon {
      animation: spin 1s linear infinite;
      color: #c084fc;
    }

    span {
      color: #a0a0c0;
    }
  }

  .verification-section {
    width: 100%;
    margin-top: 15px;

    .el-alert {
      margin-bottom: 12px;
      background: rgba(245, 158, 11, 0.1);
      border-color: rgba(245, 158, 11, 0.3);
    }
  }

  .qr-instructions {
    margin-top: 15px;
    color: #a0a0c0;
    font-size: 13px;

    p {
      margin: 5px 0;
    }

    .auth-instruction {
      strong {
        color: #c084fc;
      }
    }
  }
}

/* 加载动画 */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

:deep(.el-loading-mask) {
  background: rgba(18, 14, 33, 0.8);
  backdrop-filter: blur(4px);
}

:deep(.el-loading-spinner .circular) {
  border-color: #9d4edd;
}

:deep(.el-loading-spinner .path) {
  stroke: #9d4edd;
}

:deep(.el-loading-spinner .el-loading-text) {
  color: #c084fc;
}

/* 消息提示样式覆盖 */
:deep(.el-message) {
  background: rgba(18, 14, 33, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 12px;

  .el-message__content {
    color: #fff;
  }
}

:deep(.el-message--success) {
  .el-message__content {
    color: #4ade80;
  }
}

:deep(.el-message--warning) {
  .el-message__content {
    color: #fbbf24;
  }
}

:deep(.el-message--error) {
  .el-message__content {
    color: #f87171;
  }
}

/* 通知提示样式 */
:deep(.el-notification) {
  background: rgba(18, 14, 33, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 12px;

  .el-notification__title {
    color: #fff;
  }

  .el-notification__content {
    color: #a0a0c0;
  }
}

/* 响应式 */
@media (max-width: 768px) {
  .license-info {
    width: 100%;
    justify-content: center;
  }

  .batch-actions {
    flex-direction: column;

    .el-button {
      width: 100%;
    }
  }
}

@media (max-width: 480px) {
  .btn-text {
    display: none;
  }

  .search-btn .btn-text,
  .add-btn .btn-text {
    display: inline;
  }
}
</style>